import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Trophy,
  ArrowRight,
  Loader2,
  ClipboardCheck,
  Sparkles,
  Car,
  FileText,
  Camera,
} from 'lucide-react';

const scoreGradient = (s) =>
  s >= 90 ? 'from-emerald-400 via-green-500 to-teal-500'
    : s >= 70 ? 'from-amber-400 via-orange-500 to-yellow-500'
      : 'from-rose-400 via-red-500 to-pink-500';

const STATUS_META = {
  pass: { label: 'PASSED', chip: 'bg-brand-100 text-brand-700' },
  fail: { label: 'FAILED', chip: 'bg-danger-100 text-danger-700' },
  attention: { label: 'ATTENTION', chip: 'bg-accent-100 text-accent-700' },
  pending: { label: 'PENDING', chip: 'bg-slate-100 text-slate-500' },
};

const RECOMMENDATIONS = {
  blocked: {
    text: 'DO NOT ACCEPT DELIVERY — critical issues must be resolved',
    cls: 'bg-danger-50 border-danger-200 text-danger-700',
  },
  caution: {
    text: 'PROCEED WITH CAUTION — minor issues noted, get written acknowledgment',
    cls: 'bg-accent-50 border-accent-200 text-accent-700',
  },
  incomplete: {
    text: 'INCOMPLETE INSPECTION — remaining items should be checked before deciding',
    cls: 'bg-slate-50 border-slate-200 text-slate-600',
  },
  ready: {
    text: 'READY FOR DELIVERY — vehicle passed inspection',
    cls: 'bg-brand-50 border-brand-200 text-brand-700',
  },
};

const ItemCard = ({ item, tone }) => {
  const meta = STATUS_META[item.status] || STATUS_META.pending;
  return (
    <div
      className={`rounded-2xl border-2 p-4 ${
        tone === 'danger'
          ? 'bg-danger-50 border-danger-200'
          : tone === 'accent'
            ? 'bg-accent-50 border-accent-200'
            : 'bg-white border-slate-200'
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="font-semibold text-slate-800">{item.title}</p>
          {item.section && <p className="text-xs text-slate-500 mt-0.5">{item.section}</p>}
        </div>
        <span className={`px-2 py-1 rounded-full text-[10px] font-bold flex-shrink-0 ${meta.chip}`}>
          {meta.label}
        </span>
      </div>
      {item.note && (
        <p className="text-sm text-slate-700 mt-2 bg-white rounded-xl p-3 flex items-start gap-2">
          <FileText className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
          <span>{item.note}</span>
        </p>
      )}
      {item.photos?.length > 0 && (
        <div className="grid grid-cols-3 gap-2 mt-3">
          {item.photos.map((src, p) => (
            <img key={p} src={src} alt="Evidence" className="aspect-square object-cover rounded-xl" />
          ))}
        </div>
      )}
      {item.morePhotos > 0 && (
        <p className="text-[11px] text-slate-400 mt-2 flex items-center gap-1">
          <Camera className="w-3 h-3" />
          +{item.morePhotos} more photo{item.morePhotos > 1 ? 's' : ''} in the full PDF report
        </p>
      )}
    </div>
  );
};

const SharedReportPage = () => {
  const { id } = useParams();
  const [report, setReport] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    fetch(`/api/get-report?id=${encodeURIComponent(id)}`)
      .then((r) => {
        if (!r.ok) throw new Error(r.status === 404 ? 'This report link is invalid or has expired.' : 'Could not load this report.');
        return r.json();
      })
      .then((d) => active && setReport(d))
      .catch((e) => active && setError(e.message));
    return () => { active = false; };
  }, [id]);

  if (error) {
    return (
      <div className="min-h-screen hero-pattern flex items-center justify-center p-6">
        <div className="text-center max-w-sm">
          <p className="text-slate-700 font-semibold">{error}</p>
          <Link to="/" className="inline-flex items-center gap-2 mt-5 gradient-animated text-white font-bold py-3 px-5 rounded-2xl btn-touch">
            Make your own free PDI <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="min-h-screen hero-pattern flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-brand-500 animate-spin" />
      </div>
    );
  }

  const { car, score } = report;
  const isV2 = report.v >= 2;
  const carName = [car?.make, car?.model, car?.variant].filter(Boolean).join(' ') || 'Car';

  // v2 payloads carry full sections; v1 (older links) carried a flat issues list.
  const sections = isV2 ? report.sections || [] : [];
  const flatItems = isV2
    ? sections.flatMap((s) => s.items.map((i) => ({ ...i, section: s.title })))
    : (report.issues || []).map((i) => ({ ...i, status: i.status || 'fail' }));
  const critical = flatItems.filter((i) => i.status === 'fail');
  const warnings = flatItems.filter((i) => i.status === 'attention' || (!isV2 && i.status !== 'fail'));
  const rec = isV2 ? RECOMMENDATIONS[report.recommendation] : null;

  const carInfo = [
    car?.color && ['Color', car.color],
    car?.vin && ['VIN', car.vin],
    car?.dealer && ['Dealer', car.dealer],
  ].filter(Boolean);

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-30 glass border-b border-slate-200/50 shadow-sm">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src="/car-icon.svg" alt="Car PDI" className="w-10 h-10 rounded-xl shadow-lg" />
            <div>
              <h1 className="font-display font-bold text-slate-800 text-lg leading-tight flex items-center gap-1.5">
                Car PDI <Sparkles className="w-4 h-4 text-amber-500" />
              </h1>
              <p className="text-[10px] text-slate-500 font-medium tracking-wide">SHARED INSPECTION REPORT</p>
            </div>
          </Link>
          <Link to="/checklist" className="text-sm font-bold gradient-animated text-white px-4 py-2 rounded-xl btn-touch">
            Start free
          </Link>
        </div>
      </header>

      <main className="flex-1 max-w-3xl mx-auto w-full px-4 py-6">
        <p className="text-sm text-slate-500">Pre-delivery inspection report for</p>
        <h2 className="font-display text-2xl font-bold text-slate-800">{carName}</h2>

        {/* Vehicle info */}
        {carInfo.length > 0 && (
          <div className="mt-3 bg-white rounded-2xl border-2 border-slate-100 p-4 flex flex-wrap gap-x-6 gap-y-1">
            {carInfo.map(([label, value]) => (
              <p key={label} className="text-sm text-slate-600">
                <span className="font-semibold text-slate-400 text-xs uppercase mr-1.5">{label}</span>
                {value}
              </p>
            ))}
          </div>
        )}

        {/* Score */}
        <div className={`relative overflow-hidden bg-gradient-to-br ${scoreGradient(score.percentage)} rounded-3xl p-6 text-white shadow-2xl my-5`}>
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
          <div className="relative flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Trophy className="w-5 h-5 text-white/80" />
                <p className="text-white/80 text-sm font-semibold">PDI Score</p>
              </div>
              <p className="text-6xl font-display font-bold">{score.percentage}<span className="text-3xl">%</span></p>
              <p className="text-white/70 text-sm mt-2">{score.checked} of {score.total} items checked</p>
            </div>
          </div>
        </div>

        {/* Recommendation */}
        {rec && (
          <div className={`rounded-2xl border-2 p-4 mb-5 font-bold text-sm ${rec.cls}`}>
            {rec.text}
          </div>
        )}

        {/* Stat row */}
        <div className="grid grid-cols-3 gap-3 mb-6">
          <div className="bg-white rounded-2xl p-4 border-2 border-brand-100 text-center">
            <CheckCircle2 className="w-5 h-5 text-brand-500 mx-auto mb-1" />
            <p className="text-2xl font-bold text-brand-600">{score.pass}</p>
            <p className="text-[11px] text-slate-500 font-semibold">PASSED</p>
          </div>
          <div className="bg-white rounded-2xl p-4 border-2 border-danger-100 text-center">
            <XCircle className="w-5 h-5 text-danger-500 mx-auto mb-1" />
            <p className="text-2xl font-bold text-danger-600">{score.fail}</p>
            <p className="text-[11px] text-slate-500 font-semibold">FAILED</p>
          </div>
          <div className="bg-white rounded-2xl p-4 border-2 border-accent-100 text-center">
            <AlertTriangle className="w-5 h-5 text-accent-500 mx-auto mb-1" />
            <p className="text-2xl font-bold text-accent-600">{score.attention}</p>
            <p className="text-[11px] text-slate-500 font-semibold">ATTENTION</p>
          </div>
        </div>

        {/* Critical issues & warnings */}
        {[{ title: 'Critical Issues', items: critical, tone: 'danger' }, { title: 'Warnings', items: warnings, tone: 'accent' }]
          .filter((g) => g.items.length > 0)
          .map((group) => (
            <div key={group.title} className="mb-6">
              <h3 className={`font-bold text-lg mb-3 ${group.tone === 'danger' ? 'text-danger-700' : 'text-accent-700'}`}>
                {group.title} ({group.items.length})
              </h3>
              <div className="space-y-3">
                {group.items.map((item, i) => (
                  <ItemCard key={i} item={item} tone={group.tone} />
                ))}
              </div>
            </div>
          ))}

        {flatItems.length === 0 && (
          <div className="bg-brand-50 border-2 border-brand-200 rounded-2xl p-5 text-center text-brand-700 font-semibold mb-6">
            No issues flagged — the car passed inspection.
          </div>
        )}

        {/* Full section-by-section detail (v2 payloads) */}
        {isV2 && sections.length > 0 && (
          <div className="mb-6">
            <h3 className="font-bold text-lg text-slate-800 mb-3 flex items-center gap-2">
              <ClipboardCheck className="w-5 h-5 text-brand-500" />
              Detailed results by section
            </h3>
            <div className="space-y-3">
              {sections.map((section) => (
                <div key={section.title} className="bg-white rounded-2xl border-2 border-slate-100 overflow-hidden">
                  <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between gap-3">
                    <p className="font-bold text-slate-700">{section.title}</p>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <div className="w-20 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full ${section.percentage === 100 ? 'bg-brand-500' : 'bg-accent-400'}`}
                          style={{ width: `${section.percentage}%` }}
                        />
                      </div>
                      <span className="text-xs font-bold text-slate-400">
                        {section.completed}/{section.total}
                      </span>
                    </div>
                  </div>
                  <div className="p-3">
                    {section.items.length > 0 ? (
                      <div className="space-y-2">
                        {section.items.map((item, i) => (
                          <ItemCard key={i} item={item} tone={item.status === 'fail' ? 'danger' : item.status === 'attention' ? 'accent' : 'plain'} />
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-slate-400 px-1 py-1">{section.summary}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Growth CTA — every shared report links back to the app */}
        <div className="relative overflow-hidden bg-slate-900 rounded-3xl p-6 text-white text-center">
          <div className="absolute inset-0 bg-gradient-to-br from-brand-500/20 via-transparent to-teal-500/20" />
          <div className="relative">
            <div className="w-12 h-12 rounded-2xl gradient-animated flex items-center justify-center mx-auto mb-3">
              <Car className="w-6 h-6 text-white" />
            </div>
            <h3 className="font-display text-xl font-bold">Buying a new car?</h3>
            <p className="text-slate-300 text-sm mt-1 max-w-sm mx-auto">
              Run this same free 104-point pre-delivery inspection on your phone before you accept delivery.
            </p>
            <Link to="/" className="inline-flex items-center gap-2 mt-4 gradient-animated text-white font-bold py-3 px-6 rounded-2xl btn-touch">
              Make your own free PDI <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </main>

      <footer className="text-center py-6 text-xs text-slate-400">
        <Link to="/" className="hover:text-brand-600">www.car-pdi.app</Link> — free Pre-Delivery Inspection checklist
      </footer>
    </div>
  );
};

export default SharedReportPage;
